import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Shield, Filter, Download, Eye, EyeOff, Trash2, ChevronLeft, ChevronRight,
  Loader2, RefreshCw, FileText, AlertCircle, X, EraserIcon,
} from "lucide-react";

const MODE_LABEL = { solopreneur: "Solopreneur", sme: "SME", corporate: "Corporate" };
const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "abandoned", label: "Abandoned" },
];
const MODE_OPTIONS = [
  { value: "", label: "All modes" },
  { value: "solopreneur", label: "Solopreneur" },
  { value: "sme", label: "SME" },
  { value: "corporate", label: "Corporate" },
];

const PAGE_SIZE = 25;

function FunnelStages({ funnel }) {
  const stages = [
    { key: "started", label: "Started" },
    { key: "answered", label: "Answered" },
    { key: "generated", label: "Generated" },
    { key: "emailed", label: "Emailed" },
    { key: "shared", label: "Shared" },
    { key: "booked", label: "Booked" },
  ];
  return (
    <div className="flex items-center gap-1">
      {stages.map((s) => (
        <span
          key={s.key}
          title={s.label}
          className={`h-1.5 w-5 rounded-full ${
            funnel?.[s.key] ? "bg-teal-600 dark:bg-teal-500" : "bg-slate-200 dark:bg-slate-700"
          }`}
        />
      ))}
    </div>
  );
}

function StatusPill({ status }) {
  const tone = {
    in_progress: "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",
    completed: "bg-teal-50 text-teal-800 border-teal-200 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-800",
    abandoned: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
  }[status] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${tone}`}>
      {status.replace("_", " ")}
    </span>
  );
}

function DetailDrawer({ assessmentId, onClose, onDeleted, onAnonymised }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealing, setRevealing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [anonymising, setAnonymising] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState("");
  const [confirmAnon, setConfirmAnon] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/admin/efforts/${assessmentId}`);
        if (alive) setDetail(data);
      } catch (e) {
        console.error(e);
        toast.error("Could not load this effort.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [assessmentId]);

  const reveal = async () => {
    try {
      setRevealing(true);
      const { data } = await api.get(`/admin/efforts/${assessmentId}?reveal=true`);
      setDetail(data);
      toast.success("Full record revealed (logged in audit trail)");
    } catch (e) {
      console.error(e);
      toast.error("Reveal failed.");
    } finally {
      setRevealing(false);
    }
  };

  const doDelete = async () => {
    if (confirmDelete !== "DELETE") return toast.error('Type DELETE to confirm.');
    try {
      setDeleting(true);
      await api.delete(`/admin/efforts/${assessmentId}`);
      toast.success("Effort deleted (cascade).");
      onDeleted?.();
    } catch (e) {
      console.error(e);
      toast.error("Delete failed.");
    } finally {
      setDeleting(false);
    }
  };

  const doAnonymise = async () => {
    if (confirmAnon !== "ANONYMISE") return toast.error('Type ANONYMISE to confirm.');
    try {
      setAnonymising(true);
      await api.post(`/admin/efforts/${assessmentId}/anonymise`);
      toast.success("PII scrubbed. Funnel + score retained.");
      onAnonymised?.();
    } catch (e) {
      console.error(e);
      toast.error("Anonymise failed.");
    } finally {
      setAnonymising(false);
    }
  };

  const isAnon = !!detail?.anonymised;
  const isRevealed = detail?.email !== undefined;
  const answers = detail?.answers || {};

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex justify-end" onClick={onClose} data-testid="effort-drawer">
      <div
        className="w-full max-w-2xl h-full bg-white dark:bg-slate-950 shadow-2xl border-l border-slate-200 dark:border-slate-800 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-950/95 backdrop-blur flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Effort detail</p>
            <p className="font-display text-base font-semibold text-slate-900 dark:text-slate-50 mt-0.5">{detail?.organization || "—"}</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 dark:hover:text-slate-100" data-testid="drawer-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : !detail ? (
          <div className="p-8 text-sm text-slate-500">Not found.</div>
        ) : (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Mode</p>
                <p className="mt-1 text-slate-900 dark:text-slate-100 font-medium">{MODE_LABEL[detail.mode] || detail.mode}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Status</p>
                <p className="mt-1"><StatusPill status={detail.status} /></p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Industry</p>
                <p className="mt-1 text-slate-700 dark:text-slate-200">{detail.industry || "—"}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Department</p>
                <p className="mt-1 text-slate-700 dark:text-slate-200">{detail.department || "—"}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Org size</p>
                <p className="mt-1 text-slate-700 dark:text-slate-200">{detail.org_size || "—"}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Score</p>
                <p className="mt-1 text-slate-700 dark:text-slate-200">{detail.score ?? "—"}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Created</p>
                <p className="mt-1 text-slate-700 dark:text-slate-200">{detail.created_at?.slice(0, 19).replace("T", " ")}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Last activity</p>
                <p className="mt-1 text-slate-700 dark:text-slate-200">{detail.updated_at?.slice(0, 19).replace("T", " ")}</p>
              </div>
            </div>

            <div className={`rounded-lg border p-4 ${isAnon ? "border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40" : "border-slate-200 dark:border-slate-800"}`}>
              <div className="flex items-center justify-between">
                <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Contact</p>
                {isAnon ? (
                  <span data-testid="anon-pill-drawer" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-[11px] text-slate-600 dark:text-slate-300">
                    <EraserIcon className="h-3 w-3" /> anonymised{detail.anonymised_at ? ` · ${detail.anonymised_at.slice(0, 10)}` : ""}
                  </span>
                ) : !isRevealed ? (
                  <button
                    onClick={reveal}
                    disabled={revealing}
                    data-testid="reveal-btn"
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200 text-xs font-medium hover:bg-amber-100 dark:hover:bg-amber-950/60 disabled:opacity-50"
                  >
                    {revealing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                    Reveal (audit-logged)
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-400"><EyeOff className="h-3 w-3" /> Revealed — logged</span>
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Email</p>
                  <p className={`mt-1 font-mono text-[13px] ${isAnon ? "text-slate-400 dark:text-slate-500 italic" : "text-slate-900 dark:text-slate-100"}`}>{isRevealed ? (detail.email || "—") : (detail.email_masked || "—")}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Organisation</p>
                  <p className={`mt-1 ${isAnon ? "text-slate-400 dark:text-slate-500 italic" : "text-slate-900 dark:text-slate-100"}`}>{isRevealed ? (detail.organization_full || detail.organization || "—") : (detail.organization || "—")}</p>
                </div>
              </div>
            </div>

            {isRevealed && !isAnon && Object.keys(answers).length > 0 && (
              <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-4">
                <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Answers ({Object.keys(answers).length})</p>
                <div className="mt-3 space-y-3 text-sm">
                  {Object.entries(answers).map(([k, v]) => (
                    <div key={k}>
                      <p className="text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">{k}</p>
                      <p className="mt-0.5 text-slate-800 dark:text-slate-200 whitespace-pre-wrap">{typeof v === "string" ? v : JSON.stringify(v)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!isAnon && (
              <div data-testid="anonymise-card" className="rounded-lg border border-amber-200 dark:border-amber-900/60 bg-amber-50/60 dark:bg-amber-950/20 p-4">
                <div className="flex items-start gap-2">
                  <EraserIcon className="h-4 w-4 text-amber-700 dark:text-amber-300 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-900 dark:text-amber-200">Anonymise this effort</p>
                    <p className="mt-1 text-xs text-amber-800 dark:text-amber-300/90">
                      Scrubs email, organisation name, all free-text answers, and the generated plan / PDF. Keeps mode, industry, dates, and funnel/score so KPI history stays intact. Removes share-links, delivery records, and consultant requests for this effort. Cannot be undone.
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <input
                        value={confirmAnon}
                        onChange={(e) => setConfirmAnon(e.target.value)}
                        placeholder='Type "ANONYMISE"'
                        data-testid="anon-confirm-input"
                        className="h-9 px-3 rounded-md border border-amber-300 dark:border-amber-800 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100"
                      />
                      <button
                        onClick={doAnonymise}
                        disabled={confirmAnon !== "ANONYMISE" || anonymising}
                        data-testid="anon-confirm-btn"
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-amber-700 dark:bg-amber-600 text-white text-sm font-medium hover:bg-amber-600 dark:hover:bg-amber-500 disabled:opacity-50"
                      >
                        {anonymising ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <EraserIcon className="h-3.5 w-3.5" />}
                        Anonymise
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-lg border border-rose-200 dark:border-rose-900/60 bg-rose-50/50 dark:bg-rose-950/20 p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-rose-700 dark:text-rose-300 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-rose-900 dark:text-rose-200">Delete this effort</p>
                  <p className="mt-1 text-xs text-rose-800 dark:text-rose-300/90">
                    Removes the assessment plus all related share tokens, delivery records, bookings, testimonials, and renewal requests. Cannot be undone.
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <input
                      value={confirmDelete}
                      onChange={(e) => setConfirmDelete(e.target.value)}
                      placeholder='Type "DELETE"'
                      data-testid="delete-confirm-input"
                      className="h-9 px-3 rounded-md border border-rose-300 dark:border-rose-800 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100"
                    />
                    <button
                      onClick={doDelete}
                      disabled={confirmDelete !== "DELETE" || deleting}
                      data-testid="delete-confirm-btn"
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-rose-700 text-white text-sm font-medium hover:bg-rose-600 disabled:opacity-50"
                    >
                      {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      Delete cascade
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function AuditLogTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const reload = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/admin/audit-log?limit=100");
      setRows(data.rows || []);
    } catch (e) {
      console.error(e);
      toast.error("Could not load audit log.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  return (
    <div data-testid="audit-log-tab" className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-slate-500" />
          <p className="text-sm font-medium text-slate-900 dark:text-slate-50">Admin access audit trail</p>
        </div>
        <button onClick={reload} className="text-xs text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 inline-flex items-center gap-1">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>
      {loading ? (
        <div className="p-6 text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-6 text-sm text-slate-500">No access logged yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-900/50 text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
            <tr>
              <th className="text-left px-4 py-2 font-medium">When</th>
              <th className="text-left px-4 py-2 font-medium">Admin</th>
              <th className="text-left px-4 py-2 font-medium">Action</th>
              <th className="text-left px-4 py-2 font-medium">Resource</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800">
                <td className="px-4 py-2 text-[12.5px] text-slate-700 dark:text-slate-200 font-mono">{r.at?.slice(0, 19).replace("T", " ")}</td>
                <td className="px-4 py-2 text-[12.5px] text-slate-700 dark:text-slate-200">{r.admin_email}</td>
                <td className="px-4 py-2 text-[12.5px] text-slate-900 dark:text-slate-100 font-medium">{r.action}</td>
                <td className="px-4 py-2 text-[12.5px] text-slate-500 dark:text-slate-400">{r.resource_id ? r.resource_id.slice(0, 8) + "…" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


export default function AdminEfforts() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("efforts");
  const [filters, setFilters] = useState({ status: "", mode: "", from: "", to: "", q: "" });
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState({ rows: [], total: 0, kpis: null });
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    if (!user.is_admin) { navigate("/account"); return; }
  }, [user, authLoading, navigate]);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (filters.status) p.set("status", filters.status);
    if (filters.mode) p.set("mode", filters.mode);
    if (filters.from) p.set("from", new Date(filters.from).toISOString());
    if (filters.to) p.set("to", new Date(filters.to + "T23:59:59").toISOString());
    if (filters.q) p.set("q", filters.q);
    p.set("limit", String(PAGE_SIZE));
    p.set("offset", String(offset));
    return p.toString();
  }, [filters, offset]);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/admin/efforts?${queryString}`);
      setData(data);
    } catch (e) {
      console.error(e);
      toast.error("Could not load efforts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.is_admin) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString, user?.is_admin]);

  const downloadCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.set("status", filters.status);
      if (filters.mode) params.set("mode", filters.mode);
      if (filters.from) params.set("from", new Date(filters.from).toISOString());
      if (filters.to) params.set("to", new Date(filters.to + "T23:59:59").toISOString());
      if (filters.q) params.set("q", filters.q);
      const resp = await api.get(`/admin/efforts.csv?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `smartnote2self_efforts_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded (export logged).");
    } catch (e) {
      console.error(e);
      toast.error("CSV export failed.");
    }
  };

  const updateFilter = (patch) => { setOffset(0); setFilters((f) => ({ ...f, ...patch })); };
  const hasNext = offset + PAGE_SIZE < data.total;
  const hasPrev = offset > 0;

  if (authLoading || !user) return null;
  if (!user.is_admin) return null;

  return (
    <div className="min-h-screen bg-slate-50/40 dark:bg-slate-950 flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400">Admin · GDPR-aligned overview</p>
              <h1 className="mt-2 font-display text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight">
                Attempted efforts
              </h1>
              <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-300 max-w-2xl">
                Every assessment started, by mode, industry, funnel stage. Sensitive fields are masked by default; revealing PII writes an entry to the audit trail.
              </p>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <Link
                to="/admin/ai-status"
                data-testid="link-ai-status"
                className="inline-flex items-center gap-1.5 text-sm text-teal-700 dark:text-teal-400 hover:text-teal-900 dark:hover:text-teal-300 font-medium"
              >
                <Shield className="h-3.5 w-3.5" /> AI Status
              </Link>
              <Link to="/admin" className="text-sm text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100">
                ← Back to Admin Console
              </Link>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-6 flex items-center gap-1 border-b border-slate-200 dark:border-slate-800">
            {[{ k: "efforts", label: "Efforts" }, { k: "audit", label: "Audit log" }].map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`tab-${t.k}`}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  tab === t.k
                    ? "border-teal-600 dark:border-teal-500 text-slate-900 dark:text-slate-50"
                    : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "audit" ? (
            <div className="mt-6"><AuditLogTab /></div>
          ) : (
            <>
              {/* KPI strip */}
              {data.kpis && (
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpis">
                  {[
                    { k: "started", label: "Started" },
                    { k: "answered", label: "Answered" },
                    { k: "generated", label: "Plan generated" },
                    { k: "emailed", label: "Emailed" },
                    { k: "shared", label: "Shared" },
                    { k: "booked", label: "Booked" },
                  ].map((m) => {
                    const v = data.kpis[m.k] ?? 0;
                    const pct = data.kpis.started > 0 ? Math.round((v / data.kpis.started) * 100) : 0;
                    return (
                      <div key={m.k} className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3">
                        <p className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">{m.label}</p>
                        <p className="mt-1 font-display text-2xl font-semibold text-slate-900 dark:text-slate-50">{v}</p>
                        {m.k !== "started" && (
                          <p className="text-[11px] text-slate-500 dark:text-slate-400">{pct}% of started</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Filter bar */}
              <div className="mt-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
                <div className="md:col-span-2">
                  <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Search org / industry</label>
                  <input
                    value={filters.q}
                    onChange={(e) => updateFilter({ q: e.target.value })}
                    placeholder="e.g. retail, Northwind…"
                    data-testid="filter-q"
                    className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Status</label>
                  <select value={filters.status} onChange={(e) => updateFilter({ status: e.target.value })} data-testid="filter-status" className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100">
                    {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Mode</label>
                  <select value={filters.mode} onChange={(e) => updateFilter({ mode: e.target.value })} data-testid="filter-mode" className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100">
                    {MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">From</label>
                  <input type="date" value={filters.from} onChange={(e) => updateFilter({ from: e.target.value })} data-testid="filter-from" className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">To</label>
                  <input type="date" value={filters.to} onChange={(e) => updateFilter({ to: e.target.value })} data-testid="filter-to" className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100" />
                </div>
                <div className="md:col-span-6 flex items-center justify-between flex-wrap gap-2 pt-1">
                  <p className="text-xs text-slate-500 dark:text-slate-400 inline-flex items-center gap-1.5">
                    <Filter className="h-3.5 w-3.5" />
                    {data.total} effort{data.total === 1 ? "" : "s"} matching
                  </p>
                  <button
                    onClick={downloadCsv}
                    data-testid="csv-btn"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 text-xs font-medium hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    <Download className="h-3.5 w-3.5" /> Export CSV
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="mt-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden" data-testid="efforts-table">
                {loading ? (
                  <div className="p-8 text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
                ) : data.rows.length === 0 ? (
                  <div className="p-8 text-sm text-slate-500"><FileText className="h-4 w-4 inline mr-1.5 -mt-0.5" />No efforts match the current filters.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 dark:bg-slate-900/50 text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                        <tr>
                          <th className="text-left px-4 py-2.5 font-medium">Date</th>
                          <th className="text-left px-4 py-2.5 font-medium">Mode</th>
                          <th className="text-left px-4 py-2.5 font-medium">Organisation</th>
                          <th className="text-left px-4 py-2.5 font-medium">Industry</th>
                          <th className="text-left px-4 py-2.5 font-medium">Status</th>
                          <th className="text-left px-4 py-2.5 font-medium">Funnel</th>
                          <th className="text-left px-4 py-2.5 font-medium">Score</th>
                          <th className="text-left px-4 py-2.5 font-medium">Email</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.rows.map((r) => (
                          <tr
                            key={r.id}
                            onClick={() => setOpenId(r.id)}
                            data-testid={`effort-row-${r.id}`}
                            className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/60 cursor-pointer"
                          >
                            <td className="px-4 py-3 text-[12.5px] text-slate-700 dark:text-slate-200 font-mono">{r.created_at?.slice(0, 10)}</td>
                            <td className="px-4 py-3 text-[12.5px] text-slate-700 dark:text-slate-200">{MODE_LABEL[r.mode] || r.mode}</td>
                            <td className="px-4 py-3 text-[12.5px] text-slate-900 dark:text-slate-100">{r.anonymised ? <span className="text-slate-400 dark:text-slate-500 italic">{r.organization}</span> : (r.organization || <span className="text-slate-400">—</span>)}</td>
                            <td className="px-4 py-3 text-[12.5px] text-slate-700 dark:text-slate-200">{r.industry || <span className="text-slate-400">—</span>}</td>
                            <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                            <td className="px-4 py-3"><FunnelStages funnel={r.funnel} /></td>
                            <td className="px-4 py-3 text-[12.5px] text-slate-700 dark:text-slate-200">{r.score ?? "—"}</td>
                            <td className="px-4 py-3 text-[12.5px] text-slate-500 dark:text-slate-400 font-mono">
                              {r.anonymised ? (
                                <span data-testid={`anon-pill-${r.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 text-[10.5px] not-italic font-sans text-slate-600 dark:text-slate-300">
                                  <EraserIcon className="h-3 w-3" /> anonymised
                                </span>
                              ) : (r.email_masked || <span className="text-slate-400">—</span>)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {data.rows.length > 0 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-800 text-xs text-slate-500 dark:text-slate-400">
                    <span>Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, data.total)} of {data.total}</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                        disabled={!hasPrev}
                        data-testid="page-prev"
                        className="h-8 px-2.5 rounded-md border border-slate-200 dark:border-slate-700 inline-flex items-center gap-1 disabled:opacity-40"
                      >
                        <ChevronLeft className="h-3.5 w-3.5" /> Prev
                      </button>
                      <button
                        onClick={() => setOffset(offset + PAGE_SIZE)}
                        disabled={!hasNext}
                        data-testid="page-next"
                        className="h-8 px-2.5 rounded-md border border-slate-200 dark:border-slate-700 inline-flex items-center gap-1 disabled:opacity-40"
                      >
                        Next <ChevronRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>

      {openId && (
        <DetailDrawer
          assessmentId={openId}
          onClose={() => setOpenId(null)}
          onDeleted={() => { setOpenId(null); load(); }}
          onAnonymised={() => { setOpenId(null); load(); }}
        />
      )}

      <Footer />
    </div>
  );
}
